// Security utilities and input validation
export class SecurityUtils {
  // CSRF Token Management
  private static csrfToken: string | null = null;

  static generateCSRFToken(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  static setCSRFToken(token: string): void {
    this.csrfToken = token;
  }

  static getCSRFToken(): string | null {
    return this.csrfToken;
  }

  static validateCSRFToken(token: string): boolean {
    return this.csrfToken === token;
  }

  // Input Sanitization
  static sanitizeString(input: string): string {
    return input
      .replace(/[<>]/g, '') // Remove potential HTML tags
      .replace(/javascript:/gi, '') // Remove javascript: protocol
      .replace(/on\w+=/gi, '') // Remove event handlers
      .trim();
  }

  static sanitizeHTML(input: string): string {
    const div = document.createElement('div');
    div.textContent = input;
    return div.innerHTML;
  }

  // Allow-list sanitizer for simple rich-text fields (e.g. property description).
  // Keeps only basic formatting tags and strips everything else (attributes,
  // scripts, links, images, etc.) so user-submitted formatting can be stored
  // and rendered without opening up stored-XSS.
  private static readonly RICH_TEXT_ALLOWED_TAGS = new Set([
    'B', 'STRONG', 'I', 'EM', 'U', 'UL', 'OL', 'LI', 'BR', 'P', 'DIV',
  ]);

  static sanitizeRichText(input: string): string {
    const doc = new DOMParser().parseFromString(input, 'text/html');

    const clean = (node: Node): void => {
      [...node.childNodes].forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) return;

        if (child.nodeType !== Node.ELEMENT_NODE) {
          child.parentNode?.removeChild(child);
          return;
        }

        const el = child as Element;
        if (!this.RICH_TEXT_ALLOWED_TAGS.has(el.tagName)) {
          // Unwrap disallowed tags (e.g. <span>, <a>) but keep their text content
          while (el.firstChild) el.parentNode?.insertBefore(el.firstChild, el);
          el.parentNode?.removeChild(el);
          return;
        }

        // Strip all attributes (no href/src/style/on* handlers allowed)
        [...el.attributes].forEach((attr) => el.removeAttribute(attr.name));
        clean(el);
      });
    };

    clean(doc.body);
    return doc.body.innerHTML;
  }

  // Plain-text length of a sanitized rich-text value, for validation checks.
  static richTextToPlainText(input: string): string {
    const div = document.createElement('div');
    div.innerHTML = input;
    return (div.textContent || '').trim();
  }

  // Email Validation
  static isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  // Phone Number Validation (Indian format)
  static isValidPhoneNumber(phone: string): boolean {
    const phoneRegex = /^(\+91|91)?[6-9]\d{9}$/;
    return phoneRegex.test(phone.replace(/\s/g, ''));
  }

  // Password Strength Validation
  static validatePassword(password: string): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];
    
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }
    
    if (!/\d/.test(password)) {
      errors.push('Password must contain at least one number');
    }
    
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push('Password must contain at least one special character');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // XSS Prevention
  static escapeHTML(text: string): string {
    const map: { [key: string]: string } = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    };
    
    return text.replace(/[&<>"']/g, (m) => map[m]);
  }

  // SQL Injection Prevention (for any future backend integration)
  static sanitizeSQL(input: string): string {
    return input
      .replace(/[';]/g, '') // Remove quotes and semicolons
      .replace(/--/g, '') // Remove SQL comments
      .replace(/\/\*/g, '') // Remove block comments start
      .replace(/\*\//g, ''); // Remove block comments end
  }

  // Rate Limiting (Client-side)
  private static requestCounts: Map<string, { count: number; timestamp: number }> = new Map();

  static checkRateLimit(key: string, maxRequests: number = 10, windowMs: number = 60000): boolean {
    const now = Date.now();
    const record = this.requestCounts.get(key);

    if (!record) {
      this.requestCounts.set(key, { count: 1, timestamp: now });
      return true;
    }

    // Reset if window has passed
    if (now - record.timestamp > windowMs) {
      this.requestCounts.set(key, { count: 1, timestamp: now });
      return true;
    }

    // Check if limit exceeded
    if (record.count >= maxRequests) {
      return false;
    }

    // Increment count
    record.count++;
    return true;
  }

  // Session Management
  static setSecureCookie(name: string, value: string, days: number = 7): void {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;secure;samesite=strict`;
  }

  static getCookie(name: string): string | null {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    
    return null;
  }

  static deleteCookie(name: string): void {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
  }
}

// Input Validation Schemas
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

export class InputValidator {
  static validatePropertyData(data: any): ValidationResult {
    const errors: string[] = [];

    // Required fields
    if (!data.title || typeof data.title !== 'string' || data.title.trim().length === 0) {
      errors.push('Title is required');
    }

    // Price is optional, but if provided, it must be valid
    if (data.price !== undefined && data.price !== null) {
      if (typeof data.price !== 'number' || data.price < 0) {
        errors.push('Price must be a valid positive number');
      }
    }

    if (!data.location || typeof data.location !== 'string' || data.location.trim().length === 0) {
      errors.push('Location is required');
    }

    if (!data.city || typeof data.city !== 'string' || data.city.trim().length === 0) {
      errors.push('City is required');
    }

    // Validate property type
    const validTypes = ['residential', 'villa', 'house', 'land', 'commercial'];
    if (!data.propertyType || !validTypes.includes(data.propertyType)) {
      errors.push('Valid property type is required');
    }

    // Validate area
    if (!data.area || typeof data.area !== 'number' || data.area <= 0) {
      errors.push('Valid area is required');
    }

    // Validate images
    if (!data.images || !Array.isArray(data.images) || data.images.length === 0) {
      errors.push('At least one image is required');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  static validateContactForm(data: any): ValidationResult {
    const errors: string[] = [];

    if (!data.name || typeof data.name !== 'string' || data.name.trim().length === 0) {
      errors.push('Name is required');
    }

    if (!data.email || !SecurityUtils.isValidEmail(data.email)) {
      errors.push('Valid email is required');
    }

    if (data.phone && !SecurityUtils.isValidPhoneNumber(data.phone)) {
      errors.push('Valid phone number is required');
    }

    if (!data.subject || typeof data.subject !== 'string' || data.subject.trim().length === 0) {
      errors.push('Subject is required');
    }

    if (!data.message || typeof data.message !== 'string' || data.message.trim().length === 0) {
      errors.push('Message is required');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  static validateUserRegistration(data: any): ValidationResult {
    const errors: string[] = [];

    if (!data.name || typeof data.name !== 'string' || data.name.trim().length === 0) {
      errors.push('Name is required');
    }

    if (!data.email || !SecurityUtils.isValidEmail(data.email)) {
      errors.push('Valid email is required');
    }

    if (!data.password) {
      errors.push('Password is required');
    } else {
      const passwordValidation = SecurityUtils.validatePassword(data.password);
      if (!passwordValidation.isValid) {
        errors.push(...passwordValidation.errors);
      }
    }

    if (data.password !== data.confirmPassword) {
      errors.push('Passwords do not match');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}
